import isObject from 'lodash/isObject';

import BlueskySvg from '@/assets/images/channels/bluesky.svg';
import FacebookSvg from '@/assets/images/channels/facebook-light.svg';
import GaSvg from '@/assets/images/channels/ga.svg';
import InstagramSvg from '@/assets/images/channels/instagram.svg';
import LinkedInSvg from '@/assets/images/channels/linkedin.svg';
import MetaSvg from '@/assets/images/channels/meta.svg';
import PinterestSvg from '@/assets/images/channels/pinterest.svg';
import SnapchatSvg from '@/assets/images/channels/snapchat.svg';
import ThreadsSvg from '@/assets/images/channels/threads.svg';
import TikTokSvg from '@/assets/images/channels/tiktok.svg';
import TwitterSvg from '@/assets/images/channels/twitter.svg';
import YoutubeSvg from '@/assets/images/channels/youtube.svg';
import FacebookNoAccessImg from '@/assets/images/facebook-no-access.png';
import InstagramNoAccessImg from '@/assets/images/instagram-no-access.png';
import CanvaSvg from '@/assets/images/integrations/canva.svg';
import SalesforceSvg from '@/assets/images/integrations/salesforce.svg';
import LinkedinNoAccessImg from '@/assets/images/linkedin-no-access.png';
import PinterestNoAccessImg from '@/assets/images/pinterest-no-access.png';
import SnapchatConnection1x from '@/assets/images/snapchat/connection/1x.png';
import SnapchatConnection2x from '@/assets/images/snapchat/connection/2x.png';
import SnapchatConnection3x from '@/assets/images/snapchat/connection/3x.png';
import ThreadsNoAccessImg from '@/assets/images/threads-no-access.png';
import TikTokNoAccessImg from '@/assets/images/tiktok-no-access.png';
import TwitterNoAccessImg from '@/assets/images/twitter-no-access.png';
import YoutubeNoAccessImg from '@/assets/images/youtube-no-access.png';
import { BRAND, USER } from '@/models/auth/permissions.enum';

import type { PlatformConnection, PlatformNoConnectionData } from './types';

export const PLATFORM_CONNECTION_STATUS = {
  CONNECTED: 'connected',
  INVALID: 'invalid',
} as const;

export const BRAND_PLATFORM_PERMISSIONS = Object.freeze({
  FACEBOOK: [BRAND.FACEBOOK.CAN_ACCESS_FB_INSIGHTS],
  FACEBOOK_COMPETITIVE: [BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  INSTAGRAM: [BRAND.INSTAGRAM.CAN_ACCESS_INSTAGRAM],
  INSTAGRAM_BENCHMARKS: [BRAND.DASHBOARD.CAN_ACCESS_INDUSTRY_BENCHMARKS],
  INSTAGRAM_COMPETITIVE: [BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  INSTAGRAM_STORIES: [BRAND.INSTAGRAM.CAN_ACCESS_INSTAGRAM],
  INSTAGRAM_UGC: [BRAND.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  INSTAGRAM_STORIES_UGC: [BRAND.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  TIKTOK_UGC: [BRAND.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  LINKEDIN: [BRAND.LINKEDIN.CAN_ACCESS_LINKEDIN_INSIGHTS],
  META_ADS: [BRAND.ADS.CAN_ACCESS_ADS],
  PINTEREST: [BRAND.PINTEREST.CAN_ACCESS_PINT],
  TIKTOK: [BRAND.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS],
  TIKTOK_COMPETITIVE: [
    BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS,
    BRAND.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS,
  ],
  TIKTOK_ADS: [BRAND.ADS.CAN_ACCESS_TIKTOK_ADS],
  TWITTER: [BRAND.TWITTER.CAN_ACCESS_TWITTER_INSIGHTS],
  TWITTER_COMPETITIVE: [BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  YOUTUBE: [BRAND.YOUTUBE.CAN_ACCESS_YOUTUBE_INSIGHTS],
  YOUTUBE_COMPETITIVE: [BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  THREADS: [BRAND.THREADS.CAN_ACCESS_THREADS_INSIGHTS],
  SNAPCHAT: [BRAND.SNAPCHAT.CAN_ACCESS_SNAPCHAT_INSIGHTS],
});

export const USER_PLATFORM_PERMISSIONS = Object.freeze({
  FACEBOOK: [USER.FACEBOOK.CAN_ACCESS_FB_INSIGHTS],
  FACEBOOK_COMPETITIVE: [USER.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  INSTAGRAM: [USER.INSTAGRAM.CAN_ACCESS_INSTAGRAM],
  INSTAGRAM_COMPETITIVE: [USER.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  INSTAGRAM_STORIES: [USER.INSTAGRAM.CAN_ACCESS_STORIES],
  INSTAGRAM_UGC: [USER.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  INSTAGRAM_STORIES_UGC: [USER.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  TIKTOK_UGC: [USER.INSTAGRAM.CAN_ACCESS_UGC_MORE],
  LINKEDIN: [USER.LINKEDIN.CAN_ACCESS_LINKEDIN_INSIGHTS],
  META_ADS: [USER.ADS.CAN_ACCESS_ADS],
  PINTEREST: [USER.PINTEREST.CAN_ACCESS_PINT],
  TIKTOK: [USER.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS],
  TIKTOK_COMPETITIVE: [
    USER.COMPETITIVE.CAN_ACCESS_COMPETITORS,
    USER.TIKTOK.CAN_ACCESS_TIKTOK_INSIGHTS,
  ],
  TIKTOK_ADS: [USER.ADS.CAN_ACCESS_TIKTOK_ADS],
  TWITTER: [USER.TWITTER.CAN_ACCESS_TWITTER_INSIGHTS],
  TWITTER_COMPETITIVE: [USER.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  YOUTUBE: [USER.YOUTUBE.CAN_ACCESS_YOUTUBE_INSIGHTS],
  YOUTUBE_COMPETITIVE: [USER.COMPETITIVE.CAN_ACCESS_COMPETITORS],
  THREADS: [USER.THREADS.CAN_ACCESS_THREADS_INSIGHTS],
  SNAPCHAT: [USER.SNAPCHAT.CAN_ACCESS_SNAPCHAT_INSIGHTS],
});

const platformConnections = {
  FACEBOOK: {
    value: 'facebook',
    sandbox: 'facebook_sandbox',
    name: 'Instagram',
    img: InstagramSvg,
    channel: 'instagram',
    SCOPES: {
      INSTAGRAM_CONTENT_PUBLISH: {
        value: 'instagram_content_publish',
      },
      INSTAGRAM_MANAGE_COMMENTS: {
        value: 'instagram_manage_comments',
      },
      INSTAGRAM_MANAGE_MESSAGES: {
        value: 'instagram_manage_messages',
      },
    },
    OPTIONAL_SCOPES: {
      INSTAGRAM_BRANDED_CONTENT_ADS_BRAND: {
        value: 'instagram_branded_content_ads_brand',
      },
    },
  },
  FACEBOOK_PAGE: {
    value: 'facebook_page',
    name: 'Facebook',
    img: FacebookSvg,
    channel: 'facebook',
  },
  FACEBOOK_ANALYTICS: {
    value: 'facebook_analytics',
    name: 'Facebook',
    img: FacebookSvg,
    channel: 'facebook',
  },
  // TODO sc-106472: Remove this legacy copy
  FACEBOOK_ADS: {
    value: 'facebook_ads',
    name: 'Facebook Ads',
    img: FacebookSvg,
    channel: 'facebook',
  },
  // TODO sc-106472: Remove _new and make this the default
  FACEBOOK_ADS_NEW: {
    value: 'facebook_ads',
    name: 'Meta Ads',
    img: MetaSvg,
    iconName: 'meta',
    channel: 'meta',
    connectDisabledTooltip:
      'You need the “Manage Meta Ads” permission in order to connect this account.',
    disconnectDisabledTooltip:
      'You need the “Manage Meta Ads” permission in order to disconnect this account.',
    banners: {
      connect: {
        message:
          'Connect your Meta Ads account for promoted post and ad insights, plus ad comments in Community',
      },
      reconnect: {
        message: 'Meta Ads not connected, data may not be current',
      },
    },
  },
  INSTAGRAM_PAGE: {
    value: 'instagram_page',
    sandbox: 'instagram_page_sandbox',
    name: 'Instagram',
    img: InstagramSvg,
    channel: 'instagram',
  },
  INSTAGRAM_INFLUENCER: {
    value: 'instagram_influencer',
  },
  PINTEREST: {
    value: 'pinterest',
    name: 'Pinterest',
    img: PinterestSvg,
    channel: 'pinterest',
  },
  PINTEREST_V5: {
    value: 'pinterest_v5',
    name: 'Pinterest',
    img: PinterestSvg,
    channel: 'pinterest',
  },
  TIKTOK: {
    value: 'tiktok',
    name: 'TikTok',
    img: TikTokSvg,
    channel: 'tiktok',
    SCOPES: {
      VIDEO_PUBLISH: {
        value: 'video.publish',
      },
    },
  },
  TIKTOK_CREATOR: {
    value: 'tiktok_creator',
    name: 'TikTok Creator',
    img: TikTokSvg,
    channel: 'tiktok',
  },
  TIKTOK_ADS: {
    value: 'tiktok_ads',
    name: 'TikTok Ads',
    img: TikTokSvg,
    channel: 'tiktok',
    connectDisabledTooltip:
      'You need the “Can Manage TikTok Ads” permission in order to connect this account.',
    disconnectDisabledTooltip:
      'You need the “Can Manage TikTok Ads” permission in order to disconnect this account.',
    banners: {
      connect: {
        message: 'Connect your TikTok Ads account for promoted post and ad insights',
      },
      reconnect: {
        message: 'TikTok Ads not connected, data may not be current',
      },
    },
  },
  TIKTOK_CREATOR_MARKETPLACE: {
    value: 'tiktok_creator_marketplace',
    name: 'TikTok One Marketplace',
    img: TikTokSvg,
    channel: 'tiktok',
    connectDisabledTooltip: 'You need access to Creators in order to connect this account.',
    disconnectDisabledTooltip: 'You need access to Creators in order to disconnect this account.',
  },
  TWITTER: {
    value: 'twitter',
    name: 'X',
    img: TwitterSvg,
    channel: 'x',
    SCOPES: {
      ADS_API_ACCESS: {
        value: 'ads_api_access',
      },
    },
  },
  // Outbound protocol indicator only — connections still live under the `twitter` map key.
  TWITTER_OAUTH2: {
    value: 'twitter_oauth2',
    name: 'X',
    img: TwitterSvg,
    channel: 'x',
    SCOPES: {
      ADS_API_ACCESS: {
        value: 'ads_api_access',
      },
    },
  },
  YOUTUBE: {
    value: 'youtube',
    name: 'YouTube',
    img: YoutubeSvg,
    channel: 'youtube',
  },
  GOOGLE_ANALYTICS: {
    value: 'ga',
    name: 'Google Analytics',
    img: GaSvg,
    channel: 'google-analytics',
  },
  LINKEDIN: {
    value: 'linkedin',
    name: 'LinkedIn',
    img: LinkedInSvg,
    channel: 'linkedin',
    SCOPES: {
      ORGANIZATION_FOLLOWERS: {
        value: 'r_organization_followers',
      },
    },
  },
  THREADS: {
    value: 'threads',
    name: 'Threads',
    img: ThreadsSvg,
    channel: 'threads',
  },
  BLUESKY: {
    value: 'bluesky',
    name: 'Bluesky',
    img: BlueskySvg,
    channel: 'bluesky',
  },
  SNAPCHAT: {
    value: 'snapchat',
    name: 'Snapchat',
    img: SnapchatSvg,
    channel: 'snapchat',
  },
  SALESFORCE: {
    value: 'salesforce',
    name: 'Salesforce',
    img: SalesforceSvg,
    channel: 'salesforce',
  },
  CANVA: {
    value: 'canva',
    name: 'Canva',
    img: CanvaSvg,
    channel: 'canva',
  },
} satisfies Record<string, PlatformConnection>;

export const PLATFORM_CONNECTION: {
  [Key in keyof typeof platformConnections]: (typeof platformConnections)[Key] & PlatformConnection;
} & Record<string, PlatformConnection> = platformConnections;

export const PLATFORM_CONNECTION_NAMES = {
  FACEBOOK: 'facebook_analytics',
  FACEBOOK_COMPETITIVE: 'facebook_analytics',
  INSTAGRAM: 'facebook',
  INSTAGRAM_STORY: 'facebook',
  INSTAGRAM_STORIES: 'facebook',
  INSTAGRAM_STORIES_UGC: 'facebook',
  INSTAGRAM_UGC: 'facebook',
  INSTAGRAM_COMPETITIVE: 'facebook',
  INSTAGRAM_BENCHMARKS: 'facebook',
  META_ADS: 'facebook_ads',
  YOUTUBE_COMPETITIVE: 'youtube',
  THREADS: 'threads',
  BLUESKY: 'bluesky',
  SNAPCHAT: 'snapchat',
  TIKTOK_UGC: 'tiktok',
};

// Platforms that should redirect to settings page after OAuth to display account selection popup
export const PLATFORMS_REQUIRING_SETTINGS_REDIRECT = [
  PLATFORM_CONNECTION.TIKTOK_ADS.value,
  PLATFORM_CONNECTION.FACEBOOK_ADS.value,
  PLATFORM_CONNECTION.TIKTOK_CREATOR_MARKETPLACE.value,
];

const getPlatformConnection = (
  type: string | PlatformConnection,
): PlatformConnection | undefined =>
  isObject(type)
    ? type
    : Object.values(PLATFORM_CONNECTION).find((connection) => connection.value === type);

export function extractPlatformConnectionType(
  type: PlatformConnection,
  useSandbox: boolean,
): string;
export function extractPlatformConnectionType(
  type: string | PlatformConnection,
  useSandbox: boolean,
): string | null;
export function extractPlatformConnectionType(
  type: string | PlatformConnection,
  useSandbox: boolean,
) {
  const platformConnection = getPlatformConnection(type);
  if (!platformConnection) return null;
  return (useSandbox && platformConnection.sandbox) || getPlatformTypeString(platformConnection);
}

function getPlatformTypeString(type: PlatformConnection) {
  const platformType = type.value;
  return platformType === 'google_analytics' ? 'ga' : platformType;
}

export function isSamePlatformConnection(
  left: string | PlatformConnection,
  right: string | PlatformConnection,
  useSandbox: boolean = false,
) {
  const leftType = extractPlatformConnectionType(left, useSandbox);
  const rightType = extractPlatformConnectionType(right, useSandbox);
  return leftType === rightType;
}

export function determinePlatformConnectionType(type: string | PlatformConnection) {
  if (isObject(type)) return type.value;
  return getPlatformConnection(type)?.value ?? type;
}

export const extractValue = (obj?: { value: string } | string) => (isObject(obj) ? obj.value : obj);

// What each channel's connect screen says and shows, keyed by `PlatformConnection.channel`
export const PLATFORM_NO_CONNECTION_DATA: Record<string, PlatformNoConnectionData> = {
  instagram: {
    message: 'Essential Instagram metrics at your fingertips.',
    bannerSrc: InstagramNoAccessImg,
  },
  facebook: {
    message: 'Understand and connect with your fans with the right insights.',
    bannerSrc: FacebookNoAccessImg,
  },
  linkedin: {
    message: 'Gain rich insight and analytics on metrics and trends.',
    bannerSrc: LinkedinNoAccessImg,
  },
  tiktok: {
    message: 'Understand how your entertaining content is performing.',
    bannerSrc: TikTokNoAccessImg,
  },
  pinterest: {
    message: 'Make decisions based on insights that will inspire your audience.',
    bannerSrc: PinterestNoAccessImg,
  },
  x: {
    message: 'Stay up to date with real-time data.',
    bannerSrc: TwitterNoAccessImg,
  },
  youtube: {
    message: 'Essential video-first metrics at your fingertips.',
    bannerSrc: YoutubeNoAccessImg,
  },
  threads: {
    message: 'Gain rich insight and analytics on metrics and trends.',
    bannerSrc: ThreadsNoAccessImg,
  },
  snapchat: {
    message:
      'Connect your Snapchat account to unlock detailed analytics. See what resonates with your audience and discover opportunities to grow.',
    bannerSrc: SnapchatConnection1x,
    bannerSrcset: [
      `${SnapchatConnection1x} 1x`,
      `${SnapchatConnection2x} 2x`,
      `${SnapchatConnection3x} 3x`,
    ],
  },
};
